'use client';

import { Contract } from '@ethersproject/contracts'
import { useMemo } from 'react'
import { mainNetworkChainId, shiberseContractAddresses, puppyNetChainId, shibariumChainId, sepoliaChainId } from '../constants'
import { getContract } from '../utils'
import { useAccount } from 'wagmi'
import { Web3Provider } from '@ethersproject/providers';
import SHIBASWAP_ERC20 from '../constants/abis/shibaswap_erc20.json';
import SHIBERSE_LANDSALE_ABI from '../constants/abis/shiberse_landsale.json'
import SHIBERSE_LANDSALE_ETHEREUM_ABI from '../constants/abis/shiberse_ethereum_landsale.json'
import { ChainId } from '@shibaswap/sdk';
import { abi as MERKLE_DISTRIBUTOR_ABI } from '@uniswap/merkle-distributor/build/MerkleDistributor.json'



// returns null on errors
export function useContract(contractAddress: string | undefined, ABI: any, withSignerIfPossible = true): Contract | null {
    const { address: account } = useAccount();
    const provider = typeof window !== 'undefined' && window.ethereum
        ? new Web3Provider(window.ethereum)
        : null;

    return useMemo(() => {
        if (!contractAddress || !ABI || !provider) return null;
        try {
            return getContract(contractAddress, ABI, provider, withSignerIfPossible && account ? account : undefined);
        } catch (error) {
            console.error('Failed to get contract', error);
            return null;
        }
    }, [contractAddress, ABI, provider, withSignerIfPossible, account]);
}


export function useShiberseLandSaleMainnetContract(withSignerIfPossible?: boolean): Contract | null {
    const contractAddress = shiberseContractAddresses[mainNetworkChainId]['LAND_SALE'];
    const contractAbi = SHIBERSE_LANDSALE_ETHEREUM_ABI;

    return useContract(contractAddress, contractAbi, withSignerIfPossible);
}

export function useShiberseLandSaleSeploliaContract(withSignerIfPossible?: boolean): Contract | null {
    const contractAddress = shiberseContractAddresses[sepoliaChainId]['LAND_SALE'];
    const contractAbi = SHIBERSE_LANDSALE_ETHEREUM_ABI;

    return useContract(contractAddress, contractAbi, withSignerIfPossible);
}

export function useShiberseLandSalePuppynetContract(withSignerIfPossible?: boolean): Contract | null {
    const contractAddress = shiberseContractAddresses[puppyNetChainId]['LAND_SALE'];
    const contractAbi = SHIBERSE_LANDSALE_ABI;

    return useContract(contractAddress, contractAbi, withSignerIfPossible);
}

export function useShiberseLandSaleShibariumContract(withSignerIfPossible?: boolean): Contract | null {
    const contractAddress = shiberseContractAddresses[shibariumChainId]['LAND_SALE'];
    const contractAbi = SHIBERSE_LANDSALE_ABI;

    return useContract(contractAddress, contractAbi, withSignerIfPossible);
}

export function useShibaSwapTokenContract(withSignerIfPossible?: boolean): Contract | null {
    const { chain } = useAccount();
    const chainId = chain?.id as keyof typeof shiberseContractAddresses;
    let tokenAddress: string | undefined;

    if (chainId == mainNetworkChainId) {
        tokenAddress = shiberseContractAddresses[mainNetworkChainId]['SHIB_TOKEN'];
    } else if (chainId == puppyNetChainId) {
        tokenAddress = shiberseContractAddresses[puppyNetChainId]['SHIB_TOKEN'];
    }
    else if (chainId == sepoliaChainId) {
        tokenAddress = shiberseContractAddresses[sepoliaChainId]['SHIB_TOKEN'];
    }
    else if (chainId == shibariumChainId) {
        tokenAddress = shiberseContractAddresses[shibariumChainId]['SHIB_TOKEN'];
    } else {
        tokenAddress = shiberseContractAddresses[puppyNetChainId]['SHIB_TOKEN'];
    }
    return useContract(tokenAddress, SHIBASWAP_ERC20, withSignerIfPossible);
}

export function useShibaSwapWethTokenContract(withSignerIfPossible?: boolean): Contract | null {
    const { chain } = useAccount();
    const chainId = chain?.id as keyof typeof shiberseContractAddresses;
    let tokenAddress: string | undefined;

    if (chainId == puppyNetChainId) {
        tokenAddress = shiberseContractAddresses[puppyNetChainId]['WETH_TOKEN'];
    }
    else if (chainId == shibariumChainId) {
        tokenAddress = shiberseContractAddresses[shibariumChainId]['WETH_TOKEN'];
    }
    return useContract(tokenAddress, SHIBASWAP_ERC20, withSignerIfPossible);
}


export function useShibaMerkleDistributorContract(distributorAaddress: { [chainId in ChainId]?: string }): Contract | null {
    return useContract(distributorAaddress[mainNetworkChainId as keyof typeof distributorAaddress], MERKLE_DISTRIBUTOR_ABI, true);
}
